import {
  nextTick,
  ref,
  type Ref,
} from 'vue'

import type {
  FineUploaderUpload,
  SubmissionFileUploadResponse,
} from '@/models/current-student-advanced-standing-form/api'
import {
  tooManyUploadItemsMessage,
} from '@/models/current-student-advanced-standing-form/upload'

type UploadFileCallback = (
  file: File,
  metadata: {
    qquuid: string
    qqfilename: string
    qqtotalfilesize: number
  },
) => Promise<SubmissionFileUploadResponse>

type DeleteFileCallback = (payload: { qquuid: string }) => Promise<SubmissionFileUploadResponse>

type UseFineUploaderModalUploadsOptions = {
  uploadFile: UploadFileCallback
  deleteFile: DeleteFileCallback
  uploadErrorMessage: (error: unknown) => string
  uploadDeleteErrorMessage: (error: unknown) => string
}

type UploadListRef = Ref<FineUploaderUpload[]>
type UploadCountRef = Ref<number>

export function createFineUploaderUuid(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID()
  }

  if (globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(4)
    globalThis.crypto.getRandomValues(values)
    return Array.from(values, (value) => value.toString(16).padStart(8, '0')).join('-')
  }

  return `qq-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function uploadButtonElement(event: Event): HTMLElement | null {
  const currentTarget = event.currentTarget
  if (!(currentTarget instanceof HTMLElement)) return null

  if (currentTarget.classList.contains('qq-upload-button')) return currentTarget

  return currentTarget.closest('.qq-upload-button')
}

export function isFileDrag(event: DragEvent): boolean {
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) return false

  return Array.from(dataTransfer.types).includes('Files') || dataTransfer.files.length > 0
}

export function updateUploadAttachment(
  attachment: FineUploaderUpload,
  updates: Partial<FineUploaderUpload>,
): void {
  if (attachment.status === 'canceled') return

  Object.assign(attachment, updates)
}

export function useFineUploaderModalUploads({
  uploadFile,
  deleteFile,
  uploadErrorMessage,
  uploadDeleteErrorMessage,
}: UseFineUploaderModalUploadsOptions) {
  const uploadThumbnailUrls = ref<Record<string, string>>({})
  const uploadRetryFiles = new Map<string, File>()

  function triggerUploadInput(event: Event): void {
    const uploadButton = uploadButtonElement(event)
    if (!uploadButton) return

    const input = uploadButton.querySelector<HTMLInputElement>('input[type="file"]')
    input?.click()
  }

  async function showFineUploaderAlert(
    uploaderSelector: string,
    message: string,
    messageRef: Ref<string>,
  ): Promise<void> {
    messageRef.value = message
    await nextTick()

    const dialog = document.querySelector<HTMLDialogElement>(`${uploaderSelector} dialog.qq-alert-dialog-selector`)
    if (!dialog) return

    if (typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal()
      return
    }

    dialog.setAttribute('open', '')
  }

  function closeFineUploaderAlert(uploaderSelector: string, messageRef: Ref<string>): void {
    const dialog = document.querySelector<HTMLDialogElement>(`${uploaderSelector} dialog.qq-alert-dialog-selector`)
    if (dialog?.open && typeof dialog.close === 'function') {
      dialog.close()
    } else {
      dialog?.removeAttribute('open')
    }
    messageRef.value = ''
  }

  async function showUploadLimitAlert(
    uploaderSelector: string,
    messageRef: Ref<string>,
    activeNewItems: number,
    itemLimit: number,
    addedItems = 1,
  ): Promise<void> {
    await showFineUploaderAlert(
      uploaderSelector,
      tooManyUploadItemsMessage(activeNewItems + addedItems, itemLimit),
      messageRef,
    )
  }

  function onUploadButtonKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    triggerUploadInput(event)
  }

  function setUploadButtonHover(event: Event, active: boolean): void {
    uploadButtonElement(event)?.classList.toggle('qq-upload-button-hover', active)
  }

  function setUploadButtonFocus(event: Event, active: boolean): void {
    uploadButtonElement(event)?.classList.toggle('qq-upload-button-focus', active)
  }

  function createUploadThumbnail(file: File, uuid: string): void {
    if (!file.type.startsWith('image/')) return

    const url = URL.createObjectURL(file)
    uploadThumbnailUrls.value = {
      ...uploadThumbnailUrls.value,
      [uuid]: url,
    }
  }

  function moveUploadThumbnail(fromUuid: string, toUuid: string): void {
    const url = uploadThumbnailUrls.value[fromUuid]
    if (!url || fromUuid === toUuid) return

    const nextUrls = { ...uploadThumbnailUrls.value }
    delete nextUrls[fromUuid]
    nextUrls[toUuid] = url
    uploadThumbnailUrls.value = nextUrls
  }

  function revokeUploadThumbnail(uuid?: string): void {
    if (!uuid) return

    uploadRetryFiles.delete(uuid)
    const url = uploadThumbnailUrls.value[uuid]
    if (!url) return

    URL.revokeObjectURL(url)
    const nextUrls = { ...uploadThumbnailUrls.value }
    delete nextUrls[uuid]
    uploadThumbnailUrls.value = nextUrls
  }

  function revokeUploadThumbnailsFor(attachments: FineUploaderUpload[]): void {
    for (const attachment of attachments) {
      revokeUploadThumbnail(attachment.uuid)
    }
  }

  function revokeAllUploadThumbnails(): void {
    for (const url of Object.values(uploadThumbnailUrls.value)) {
      URL.revokeObjectURL(url)
    }
    uploadThumbnailUrls.value = {}
    uploadRetryFiles.clear()
  }

  function uploadThumbnailUrl(attachment: FineUploaderUpload): string {
    return attachment.uuid ? uploadThumbnailUrls.value[attachment.uuid] ?? '' : ''
  }

  function onUploadDragEnter(event: DragEvent, dropActive: Ref<boolean>): void {
    if (!isFileDrag(event)) return

    event.preventDefault()
    event.stopPropagation()
    dropActive.value = true
  }

  function onUploadDragOver(event: DragEvent, dropActive: Ref<boolean>): void {
    if (!isFileDrag(event)) return

    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
    dropActive.value = true
  }

  function onUploadDragLeave(event: DragEvent, dropActive: Ref<boolean>): void {
    if (!isFileDrag(event)) return

    const relatedTarget = event.relatedTarget
    if (relatedTarget instanceof Node && event.currentTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    dropActive.value = false
  }

  async function onUploadDrop(
    event: DragEvent,
    dropActive: Ref<boolean>,
    uploadFiles: (files: File[]) => Promise<void>,
  ): Promise<void> {
    if (!isFileDrag(event)) return

    event.preventDefault()
    event.stopPropagation()
    dropActive.value = false

    const files = Array.from(event.dataTransfer?.files ?? [])
    if (files.length === 0) return

    await uploadFiles(files)
  }

  async function onAttachmentSelected(
    event: Event,
    uploadFiles: (files: File[]) => Promise<void>,
  ): Promise<void> {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files ?? [])
    input.value = ''

    await uploadFiles(files)
  }

  function cancelStagedUpload(attachment: FineUploaderUpload): void {
    revokeUploadThumbnail(attachment.uuid)
    attachment.status = 'canceled'
  }

  async function uploadAttachment(
    file: File,
    attachments: UploadListRef,
    uploadsInProgress: UploadCountRef,
    setError: (message: string) => void,
  ): Promise<void> {
    const qquuid = createFineUploaderUuid()
    const pendingAttachment: FineUploaderUpload = {
      uuid: qquuid,
      name: file.name,
      size: file.size,
      status: 'uploading',
    }
    uploadRetryFiles.set(qquuid, file)
    createUploadThumbnail(file, qquuid)
    const uploadIndex = attachments.value.push(pendingAttachment) - 1
    const attachment = attachments.value[uploadIndex]
    if (!attachment) return

    await sendUploadAttachment(attachment, file, uploadsInProgress, setError, 'uploading')
  }

  async function sendUploadAttachment(
    attachment: FineUploaderUpload,
    file: File,
    uploadsInProgress: UploadCountRef,
    setError: (message: string) => void,
    uploadingStatus: 'uploading' | 'retrying upload',
  ): Promise<void> {
    const qquuid = attachment.uuid ?? createFineUploaderUuid()
    attachment.uuid = qquuid
    updateUploadAttachment(attachment, {
      status: uploadingStatus,
      size: file.size,
    })
    uploadsInProgress.value += 1
    try {
      const result = await uploadFile(file, {
        qquuid,
        qqfilename: file.name,
        qqtotalfilesize: file.size,
      })

      if (result.success) {
        const finalUuid = result.uuid ?? qquuid
        moveUploadThumbnail(qquuid, finalUuid)
        uploadRetryFiles.delete(qquuid)
        uploadRetryFiles.delete(finalUuid)
        updateUploadAttachment(attachment, {
          uuid: finalUuid,
          uploadName: result.uploadName,
          status: 'upload successful',
        })
        return
      }

      updateUploadAttachment(attachment, { status: 'upload failed' })
      setError(result.error ? `Upload failed: ${result.error}` : 'Upload failed')
    } catch (error) {
      updateUploadAttachment(attachment, { status: 'upload failed' })
      setError(uploadErrorMessage(error))
    } finally {
      uploadsInProgress.value = Math.max(0, uploadsInProgress.value - 1)
    }
  }

  async function retryUploadAttachment(
    attachment: FineUploaderUpload,
    uploadsInProgress: UploadCountRef,
    setError: (message: string) => void,
  ): Promise<void> {
    if (!attachment.uuid) return

    const file = uploadRetryFiles.get(attachment.uuid)
    if (!file) {
      setError('Upload failed')
      return
    }

    await sendUploadAttachment(attachment, file, uploadsInProgress, setError, 'retrying upload')
  }

  async function deleteStagedUpload(
    attachment: FineUploaderUpload,
    setError: (message: string) => void,
  ): Promise<void> {
    if (!attachment.uuid || attachment.status !== 'upload successful') return

    attachment.status = 'deleting'
    try {
      await deleteFile({ qquuid: attachment.uuid })
      attachment.status = 'deleted'
      revokeUploadThumbnail(attachment.uuid)
    } catch (error) {
      attachment.status = 'delete failed'
      setError(uploadDeleteErrorMessage(error))
    }
  }

  return {
    triggerUploadInput,
    closeFineUploaderAlert,
    showUploadLimitAlert,
    onUploadButtonKeydown,
    setUploadButtonHover,
    setUploadButtonFocus,
    revokeUploadThumbnailsFor,
    revokeAllUploadThumbnails,
    uploadThumbnailUrl,
    onUploadDragEnter,
    onUploadDragOver,
    onUploadDragLeave,
    onUploadDrop,
    onAttachmentSelected,
    cancelStagedUpload,
    uploadAttachment,
    retryUploadAttachment,
    deleteStagedUpload,
  }
}
